// Infinite scroll for ALL collection pages (Prestige-safe, no looping, hides pagination)
document.addEventListener('DOMContentLoaded', () => {
  // Find the product grid
  const grid =
    document.querySelector('[data-product-grid]') ||
    document.querySelector('.product-grid') ||
    document.querySelector('.product-list') ||
    document.querySelector('[id*="CollectionProductGrid"]') ||
    document.querySelector('.collection .grid');

  if (!grid) return; // not a collection page with a grid

  // Mark page so CSS hooks can apply if needed
  document.documentElement.classList.add('infinite-active');

  // Pagination selector
  const pagSelector = 'nav.pagination, .pagination, [data-pagination], .pagination-wrapper, .collection__pagination';

  const getPageNum = (href) => {
    try {
      const u = new URL(href, window.location.origin);
      return parseInt(u.searchParams.get('page') || '1', 10);
    } catch {
      // Fallback if URL() fails
      const match = (href || '').match(/[\?&]page=(\d+)/);
      return match ? parseInt(match[1], 10) : 1;
    }
  };

  // Normalize a product link to "/products/<handle>" for de-dupe
  const normalizeProductKey = (href) => {
    try {
      const u = new URL(href, location.origin);
      const path = u.pathname;
      const i = path.indexOf('/products/');
      if (i >= 0) {
        const rest = path.slice(i + '/products/'.length);
        const handle = rest.split('/')[0];
        return `/products/${handle}`;
      }
      return path;
    } catch {
      const i = (href || '').indexOf('/products/');
      return i >= 0 ? '/products/' + href.slice(i + '/products/'.length).split('/')[0] : href;
    }
  };

  // Hide any visible pagination (JS-level so it stays hidden after DOM changes)
  const hidePagination = () => {
    document
      .querySelectorAll(pagSelector)
      .forEach(el => el.style.setProperty('display', 'none', 'important'));
  };

  // Track last page loaded + already appended product URLs
  let lastLoadedPage = getPageNum(window.location.href);
  const seen = new Set(
    Array.from(grid.querySelectorAll('a[href*="/products/"]')).map(a =>
      normalizeProductKey(a.href || a.getAttribute('href'))
    )
  );

  let loading = false;
  let observer;

  // Loader + sentinel
  const pagContainer = document.querySelector(pagSelector);
  const container = pagContainer?.parentNode || grid.parentNode;

  const loader = document.createElement('div');
  loader.id = 'infinite-loader';
  loader.textContent = 'Loading…';
  loader.style.cssText = 'text-align:center; padding:12px 0; display:none;';
  container.appendChild(loader);

  const sentinel = document.createElement('div');
  sentinel.id = 'infinite-sentinel';
  container.appendChild(sentinel);

  hidePagination(); // hide immediately on first load

  function stopInfinite(msg = 'All items loaded') {
    if (observer) observer.disconnect();
    loader.textContent = msg;
    loader.style.display = 'block';
  }

  // Find the link for the next page, based on current pagination + lastLoadedPage
  function getNextPageLink() {
    const pag = document.querySelector(pagSelector);
    if (!pag) return null;

    // 1) Try explicit "next" hooks first
    let link =
      pag.querySelector('a[rel="next"]') ||
      pag.querySelector('a[aria-label*="Next"]') ||
      pag.querySelector('.pagination__item--next a, a.pagination__item--next');

    if (link) return link;

    // 2) Fallback: look at all ?page= links and pick the smallest page > lastLoadedPage
    const links = Array.from(pag.querySelectorAll('a[href*="page="]'));
    let candidate = null;
    let bestPage = Infinity;

    links.forEach(a => {
      const href = a.getAttribute('href') || a.href;
      const p = getPageNum(href);
      if (p > lastLoadedPage && p < bestPage) {
        bestPage = p;
        candidate = a;
      }
    });

    return candidate;
  }

  async function loadNextPage() {
    const link = getNextPageLink();
    if (!link || loading) return;

    const nextHref = link.getAttribute('href') || link.href;
    const nextPage = getPageNum(nextHref);

    loading = true;
    loader.style.display = 'block';

    try {
      const res = await fetch(nextHref, { credentials: 'same-origin' });
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Grab the next page's grid
      const nextGrid =
        doc.querySelector('[data-product-grid]') ||
        doc.querySelector('.product-grid') ||
        doc.querySelector('.product-list') ||
        doc.querySelector('[id*="CollectionProductGrid"]') ||
        doc.querySelector('.collection .grid');

      let appended = 0;

      if (nextGrid) {
        Array.from(nextGrid.children).forEach(el => {
          const a = el.querySelector && el.querySelector('a[href*="/products/"]');
          const key = a ? normalizeProductKey(a.getAttribute('href') || a.href) : null;

          if (!key || !seen.has(key)) {
            if (key) seen.add(key);
            grid.appendChild(el);
            appended++;
          }
        });
      }

      // Replace/advance pagination so it reflects the new page range
      const newPag = doc.querySelector(pagSelector);
      const oldPag = document.querySelector(pagSelector);
      if (oldPag && newPag) oldPag.replaceWith(newPag);
      else if (oldPag && !newPag) oldPag.remove();

      hidePagination(); // keep pagination hidden after replacement

      lastLoadedPage = nextPage;

      // If there is no further next page, stop
      if (!getNextPageLink()) return stopInfinite();

      // If somehow nothing new was appended but a next page exists, keep going on next scroll
      setupObserver(true);

      // Re-init product card JS if theme exposes a hook
      if (window.Prestige && typeof window.Prestige.reinitProductCards === 'function') {
        window.Prestige.reinitProductCards(grid);
      }
    } catch (e) {
      console.error('Infinite scroll error:', e);
      stopInfinite('Error loading more items');
    } finally {
      loading = false;
      if (getNextPageLink()) loader.style.display = 'none';
    }
  }

  function setupObserver(reset = false) {
    if (observer && reset) observer.disconnect();

    if (!getNextPageLink()) return stopInfinite();

    observer = new IntersectionObserver(
      entries => entries.forEach(entry => entry.isIntersecting && loadNextPage()),
      { rootMargin: '600px 0px', threshold: 0 }
    );
    observer.observe(sentinel);
  }

  setupObserver();
});
